import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Search, Ticket, X } from "lucide-react";
import type {
  CustomerSearchHit,
  SearchMatchField,
  TicketSearchHit,
} from "@/api/customers";
import type { ChampSearchHit } from "@/api/search";
import type { TicketState } from "@/api/tickets";
import { ErrorState } from "@/components/shared/error-state";
import { StatusText } from "@/components/shared/status-badge";
import {
  CUSTOMER_STATUS,
  customerCode,
} from "@/components/shared/customers/customer-display";
import { CHAMP_STATUS, champCode } from "@/components/shared/champs/champ-display";
import { useChampPanel } from "@/components/shared/champs/champ-panel-params";
import { useCustomerPanel } from "@/components/shared/customers/customer-panel-params";
import { useTicketDock } from "@/components/shared/tickets/ticket-dock-params";
import { STATE_LABEL, STATE_TONE } from "@/components/shared/tickets/ticket-display";
import { ContactAvatar } from "@/components/shared/whatsapp/contact-avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebounced } from "@/hooks/use-debounced";
import { useIsMobile } from "@/hooks/use-media-query";
import { usePermissions } from "@/hooks/use-permissions";
import { isSearchable, useSearch } from "@/hooks/use-search";
import { Permission } from "@/lib/permissions";
import { cn } from "@/lib/utils";

const FIELD_LABEL: Record<SearchMatchField, string> = {
  phone: "Phone",
  whatsapp: "WhatsApp",
  alternate: "Alternate number",
  id: "Customer number",
  car: "Car",
  name: "Name",
};

const CHAMP_FIELD_LABEL: Record<ChampSearchHit["match"]["field"], string> = {
  phone: "Phone",
  whatsapp: "WhatsApp",
  alternative: "Alternative number",
  emergency: "Emergency number",
  id: "Champ number",
  code: "Code",
  name: "Name",
};

type Entry =
  | { type: "customer"; id: string; hit: CustomerSearchHit }
  | { type: "champ"; id: string; hit: ChampSearchHit }
  | { type: "ticket"; id: string; hit: TicketSearchHit };

const words = (query: string) => query.trim().toLowerCase().split(/\s+/).filter(Boolean);

/** Marks where the typed words appear — the same mark the dropdown pickers use. */
function Marked({ text, query }: { text: string; query: string }) {
  const parts = words(query);
  if (!parts.length) return <>{text}</>;
  const escaped = parts.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pieces = text.split(new RegExp(`(${escaped.join("|")})`, "gi"));
  return (
    <>
      {pieces.map((piece, index) =>
        index % 2 === 1 ? (
          <mark key={index} className="rounded-sm bg-primary/15 text-foreground">
            {piece}
          </mark>
        ) : (
          <Fragment key={index}>{piece}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * The top bar's one search — customers, champs and tickets: one field, results
 * in a popover over the page. `/` or Ctrl/Cmd+K focuses it. Enter opens the
 * customer's or champ's panel (or the ticket's); Ctrl/Cmd+Enter opens
 * `/customers/<id>` or `/champs/<id>` in a new tab. Arrow keys move through
 * the results from the field; the list is one tab stop. Rendered with
 * `customers.view` or `champs.view`; a group the admin may not open is empty.
 */
export function GlobalSearch() {
  const { can } = usePermissions();
  const things = [
    can(Permission.CUSTOMERS_VIEW) ? "customer" : null,
    can(Permission.CHAMPS_VIEW) ? "champ" : null,
    can(Permission.TICKETS_VIEW) ? "ticket" : null,
  ].filter(Boolean);
  const fieldLabel = `Find a ${things.length > 1 ? `${things.slice(0, -1).join(", ")} or ${things[things.length - 1]}` : things[0]}`;
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusRequest, setFocusRequest] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const panel = useCustomerPanel();
  const champPanel = useChampPanel();
  const ticketDock = useTicketDock();

  const debounced = useDebounced(query, 250);
  const search = useSearch(debounced);
  const searchable = isSearchable(query);
  const settled = query.trim() === debounced.trim();
  const loading =
    searchable && (!settled || search.isPending || search.isPlaceholderData);

  const entries = useMemo<Entry[]>(() => {
    if (!searchable || loading || !search.data) return [];
    return [
      ...search.data.customers.map((hit): Entry => ({
        type: "customer",
        id: `customer-${hit.id}`,
        hit,
      })),
      ...search.data.champs.map((hit): Entry => ({
        type: "champ",
        id: `champ-${hit.id}`,
        hit,
      })),
      ...search.data.tickets.map((hit): Entry => ({
        type: "ticket",
        id: `ticket-${hit.id}`,
        hit,
      })),
    ];
  }, [search.data, searchable, loading]);

  useEffect(() => setActiveIndex(0), [entries]);

  // `/` and Ctrl/Cmd+K, from anywhere that is not a text field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
      const slash =
        event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey;
      const palette = event.key.toLowerCase() === "k" && (event.ctrlKey || event.metaKey);
      if ((slash && !typing) || palette) {
        // Behind a modal (the customer panel, a dialog) the field cannot be used.
        // Radix dialogs carry data-state="open" but never aria-modal; the docked
        // panels (aria-modal="false", no data-state) do not count.
        if (document.querySelector("[role=dialog][data-state=open]")) return;
        event.preventDefault();
        if (isMobile) setExpanded(true);
        setFocusRequest((value) => value + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile]);

  useEffect(() => {
    if (focusRequest === 0) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [focusRequest, expanded]);

  const finish = useCallback(() => {
    setQuery("");
    setOpen(false);
    setExpanded(false);
  }, []);

  const pick = (entry: Entry, newTab: boolean) => {
    if (entry.type === "customer") {
      if (newTab) {
        window.open(`/customers/${entry.hit.id}`, "_blank", "noopener,noreferrer");
        return;
      }
      panel.open(entry.hit.id);
    } else if (entry.type === "champ") {
      if (newTab) {
        window.open(`/champs/${entry.hit.id}`, "_blank", "noopener,noreferrer");
        return;
      }
      champPanel.open(entry.hit.id);
    } else {
      ticketDock.open({ kind: "ticket", id: entry.hit.id });
    }
    finish();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!entries.length) return;
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index) => (index + step + entries.length) % entries.length);
    } else if (event.key === "Enter") {
      const entry = entries[activeIndex];
      if (!entry) return;
      event.preventDefault();
      pick(entry, event.ctrlKey || event.metaKey);
    } else if (event.key === "Escape") {
      // Clears first, then leaves the field — as every picker does.
      event.preventDefault();
      if (query) {
        setQuery("");
      } else {
        setOpen(false);
        setExpanded(false);
        inputRef.current?.blur();
      }
    }
  };

  const activeId = entries[activeIndex]?.id;
  const showPopover = open && query.trim() !== "";
  const customers = entries.filter((entry) => entry.type === "customer");
  const champs = entries.filter((entry) => entry.type === "champ");
  const tickets = entries.filter((entry) => entry.type === "ticket");

  const optionProps = (entry: Entry) => ({
    id: `${listId}-${entry.id}`,
    role: "option" as const,
    "aria-selected": entry.id === activeId,
    onMouseEnter: () => setActiveIndex(entries.indexOf(entry)),
    onClick: (event: React.MouseEvent) => pick(entry, event.ctrlKey || event.metaKey),
  });

  const field = (
    <div ref={anchorRef} className={cn("relative", isMobile ? "flex-1" : "w-44 lg:w-72")}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={fieldLabel}
        aria-expanded={showPopover}
        aria-controls={showPopover ? `${listId}-list` : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          showPopover && activeId ? `${listId}-${activeId}` : undefined
        }
        autoComplete="off"
        spellCheck={false}
        placeholder={fieldLabel}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          if (isMobile && !query) setExpanded(false);
        }}
        onKeyDown={onKeyDown}
        className="pl-9 pr-9"
      />
      {query ? (
        <button
          type="button"
          aria-label="Clear the search"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setQuery("");
            inputRef.current?.focus();
          }}
          className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      ) : (
        <kbd
          aria-hidden="true"
          className="pointer-events-none absolute right-2 top-1/2 hidden h-5 -translate-y-1/2 items-center rounded border border-border bg-muted px-1.5 font-sans text-xs text-muted-foreground lg:inline-flex"
        >
          /
        </kbd>
      )}
    </div>
  );

  let body: ReactNode;
  if (!searchable) {
    body = (
      <p className="px-3 py-4 text-sm text-muted-foreground">
        Keep typing: at least 3 letters, a phone number, a car number or a number like
        CX-1234 or CH-1234.
      </p>
    );
  } else if (loading) {
    body = (
      <div className="divide-y divide-border" role="status">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="flex items-center gap-3 px-3 py-2.5">
            <Skeleton className="size-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3.5 w-3/4" />
            </div>
          </div>
        ))}
        <span className="sr-only">Searching</span>
      </div>
    );
  } else if (search.isError) {
    body = (
      <ErrorState
        title="Couldn’t search"
        error={search.error}
        onRetry={() => void search.refetch()}
        className="py-6"
      />
    );
  } else if (!entries.length) {
    body = (
      <div className="space-y-1 px-3 py-6 text-center">
        <p className="text-sm font-medium">Nothing found for “{query.trim()}”</p>
        <p className="text-sm text-muted-foreground">
          Try the whole phone number, a car number or part of the name.
        </p>
      </div>
    );
  } else {
    body = (
      <div
        id={`${listId}-list`}
        role="listbox"
        aria-label="Search results"
        className="max-h-96 overflow-y-auto py-1"
      >
        {customers.length ? (
          <div role="group" aria-labelledby={`${listId}-customers`}>
            <p
              id={`${listId}-customers`}
              className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground"
            >
              Customers
            </p>
            {customers.map((entry) =>
              entry.type === "customer" ? (
                <CustomerOption
                  key={entry.id}
                  hit={entry.hit}
                  query={query}
                  active={entry.id === activeId}
                  {...optionProps(entry)}
                />
              ) : null,
            )}
          </div>
        ) : null}
        {champs.length ? (
          <div role="group" aria-labelledby={`${listId}-champs`}>
            <p
              id={`${listId}-champs`}
              className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground"
            >
              Champs
            </p>
            {champs.map((entry) =>
              entry.type === "champ" ? (
                <ChampOption
                  key={entry.id}
                  hit={entry.hit}
                  query={query}
                  active={entry.id === activeId}
                  {...optionProps(entry)}
                />
              ) : null,
            )}
          </div>
        ) : null}
        {tickets.length ? (
          <div role="group" aria-labelledby={`${listId}-tickets`}>
            <p
              id={`${listId}-tickets`}
              className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground"
            >
              Tickets
            </p>
            {tickets.map((entry) =>
              entry.type === "ticket" ? (
                <TicketOption
                  key={entry.id}
                  hit={entry.hit}
                  active={entry.id === activeId}
                  {...optionProps(entry)}
                />
              ) : null,
            )}
          </div>
        ) : null}
      </div>
    );
  }

  const popover = (
    <Popover open={showPopover}>
      <PopoverAnchor asChild>{field}</PopoverAnchor>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0 md:w-96"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        // Keep the caret in the field when a result or the scrollbar is pressed.
        onMouseDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
      >
        {body}
      </PopoverContent>
    </Popover>
  );

  if (isMobile) {
    return expanded ? (
      <div className="absolute inset-0 z-10 flex items-center gap-2 bg-background px-3">
        {popover}
        <Button variant="ghost" size="sm" onClick={finish}>
          Cancel
        </Button>
      </div>
    ) : (
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={fieldLabel}
        onClick={() => {
          setExpanded(true);
          setFocusRequest((value) => value + 1);
        }}
        className="ml-auto"
      >
        <Search aria-hidden="true" />
      </Button>
    );
  }
  return <div className="ml-auto">{popover}</div>;
}

interface OptionDomProps {
  id: string;
  role: "option";
  "aria-selected": boolean;
  onMouseEnter: () => void;
  onClick: (event: React.MouseEvent) => void;
}

function CustomerOption({
  hit,
  query,
  active,
  ...dom
}: { hit: CustomerSearchHit; query: string; active: boolean } & OptionDomProps) {
  const status = CUSTOMER_STATUS[hit.status];
  const byName = hit.match.field === "name";
  const matchLine = byName ? hit.phone : hit.match.value;
  return (
    <div
      {...dom}
      className={cn(
        "flex cursor-pointer items-center gap-3 px-3 py-2",
        active && "bg-secondary",
      )}
    >
      <ContactAvatar name={hit.name} className="size-8" />
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2 text-sm">
          <span className="truncate font-medium">
            {byName ? <Marked text={hit.name} query={query} /> : hit.name}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {customerCode(hit.id)}
          </span>
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {byName ? null : `${FIELD_LABEL[hit.match.field]} `}
          {byName ? (
            matchLine
          ) : (
            <mark className="rounded-sm bg-primary/15 text-foreground">{matchLine}</mark>
          )}
          {hit.societyName ? ` · ${hit.societyName}` : ""}
          {hit.openTickets > 0 ? ` · ${hit.openTickets} open` : ""}
        </p>
      </div>
      {hit.isDeleted ? (
        <StatusText tone="destructive" label="Deleted" className="shrink-0 text-xs" />
      ) : (
        <StatusText
          tone={status.tone}
          label={status.label}
          className="shrink-0 text-xs"
        />
      )}
    </div>
  );
}

/** The one word for a champ's standing: deactivated and leads first, then his role or status. */
function champStanding(hit: ChampSearchHit) {
  if (hit.isDeactivated) return { label: "Deactivated", tone: "destructive" as const };
  if (hit.isLead) return { label: "Lead", tone: "neutral" as const };
  if (hit.role === "area_manager")
    return { label: "Area manager", tone: "info" as const };
  return CHAMP_STATUS[hit.status];
}

function ChampOption({
  hit,
  query,
  active,
  ...dom
}: { hit: ChampSearchHit; query: string; active: boolean } & OptionDomProps) {
  const standing = champStanding(hit);
  const byName = hit.match.field === "name";
  return (
    <div
      {...dom}
      className={cn(
        "flex cursor-pointer items-center gap-3 px-3 py-2",
        active && "bg-secondary",
      )}
    >
      <ContactAvatar name={hit.name} className="size-8" />
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2 text-sm">
          <span className="truncate font-medium">
            {byName ? <Marked text={hit.name} query={query} /> : hit.name}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {champCode(hit.id, hit.code)}
          </span>
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {byName ? (
            hit.phone
          ) : (
            <>
              {CHAMP_FIELD_LABEL[hit.match.field]}{" "}
              <mark className="rounded-sm bg-primary/15 text-foreground">
                {hit.match.value}
              </mark>
            </>
          )}
          {hit.area ? ` · ${hit.area}` : ""}
        </p>
      </div>
      <StatusText
        tone={standing.tone}
        label={standing.label}
        className="shrink-0 text-xs"
      />
    </div>
  );
}

function TicketOption({
  hit,
  active,
  ...dom
}: { hit: TicketSearchHit; active: boolean } & OptionDomProps) {
  const state = hit.state as TicketState;
  return (
    <div
      {...dom}
      className={cn(
        "flex cursor-pointer items-center gap-3 px-3 py-2",
        active && "bg-secondary",
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary"
      >
        <Ticket className="size-4 text-muted-foreground" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2 text-sm">
          <span className="shrink-0 font-medium">{hit.number}</span>
          <span className="truncate">{hit.title || "No title"}</span>
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {hit.customer ? hit.customer.name : "No customer"}
        </p>
      </div>
      <StatusText
        tone={STATE_TONE[state] ?? "neutral"}
        label={STATE_LABEL[state] ?? hit.state}
        className="shrink-0 text-xs"
      />
    </div>
  );
}
